import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AvatarInitials } from '@/components/common/admin/AvatarInitials';
import { ADMIN_COLORS } from '@/constants/admin';
import { capitalizeFirstLetter } from '@/utils/admin/utils';
import { getLeaseStatusColor, getLeaseStatusLabel } from '@/utils/admin/residentsList/status';
import { IResidentDetailsResponse } from '@/types/admin/residentDetails';

interface ResidentDetailsHeaderProps {
  resident: Pick<IResidentDetailsResponse, 'firstName' | 'lastName' | 'leaseStatus' | 'propertyName' | 'buildingUnitNumberSpace'>;
  onClose?: () => void;
  showClose?: boolean;
}

export const ResidentDetailsHeader: React.FC<ResidentDetailsHeaderProps> = ({
  resident,
  onClose,
  showClose = true,
}) => {
  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Avatar */}
        <AvatarInitials firstName={resident.firstName} lastName={resident.lastName} size={48} />

        {/* Resident Info */}
        <View style={styles.info}>
          {/* Lease Status Badge */}
          <View style={[styles.statusBadge, { backgroundColor: getLeaseStatusColor(resident.leaseStatus) + '20' }]}>
            <Text style={[styles.statusText, { color: getLeaseStatusColor(resident.leaseStatus) }]}>
              {getLeaseStatusLabel(resident.leaseStatus)}
            </Text>
          </View>

          {/* Name */}
          <Text style={styles.name} numberOfLines={1}>
            {resident.firstName} {resident.lastName}
          </Text>

          {/* Property and Unit */}
          <Text style={styles.property} numberOfLines={1}>
            {resident.propertyName} {resident.buildingUnitNumberSpace || ''}
          </Text>
        </View>
      </View>
      {showClose && onClose && (
        <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Close">
          <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={ADMIN_COLORS.TEXT_SECONDARY} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: ADMIN_COLORS.SURFACE,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {},
  avatarText: {},
  info: {
    flex: 1,
    marginLeft: 12,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  name: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },
  property: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  closeButton: {
    padding: 6,
    marginLeft: 12,
  },
});
